import {
  AfterViewChecked, Component, ElementRef, EventEmitter,
  Input, OnChanges, OnInit, Output, signal, SimpleChanges, ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Program } from '@program/shared/program.model';
import { Organization } from '@shared/models/organization/organization.model';
import { User } from '@user/user.model';
import { take } from 'rxjs/operators';
import { DisplayConfiguration, ProgramMessage } from './program-message.model';
import { ProgramMessageService } from './program-message.service';
import { DocumentDataService } from '@shared/components/files/shared/document-data.service';
import { DocTag } from '@shared/components/files/shared/models/doc-tag.model';
import { DOCUMENT_TAG_TYPE } from '@shared/components/files/shared/document-tag-type.enum';
import { Document } from '@shared/components/files/shared/models/document.model';
import { PROGRAM_MESSAGE_TYPE } from './document-tag-type.enum';
import FileSaver from 'file-saver';
import { FileNameCheckerService } from '@shared/services/file-name-checker.service';
import { forkJoin } from 'rxjs';
import { StatusItem } from '@shared/components/status-dropdown/status-dropdown.model';
import { ProgramApplication } from '@program/shared/program-application.model';
import { EventService } from '@shared/event.service';
import { TranslateService } from '@ngx-translate/core';
import { ProgramApplicationService } from '@program/shared/program-application.service';

@Component({
  selector: 'oes-program-message-window',
  templateUrl: './message-window.component.html',
  styleUrls: ['./message-window.component.scss'],
  providers: [ProgramMessageService]
})
export class ProgramMessageWindowComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('messageViewPort', { static: false }) private messageViewPort: ElementRef;

  @Input() currentUser: User; // developer or investor
  @Input() participantOrganization: Organization; // should be Developer org
  @Input() program: Program;
  @Input() stageOneApplicationId: string;
  @Input() displayConfiguration: DisplayConfiguration = {
    displayMessagesList: true,
    displayReceiverSender: true,
    displayStatusSelector: false,
    isEvaluationMessage: false
  };
  @Input() programApplication: ProgramApplication;
  @Input() customWindowStyles: { [key: string]: string } = {};

  @Output() refresh: EventEmitter<boolean> = new EventEmitter<boolean>();

  documents: Document[];
  fileUpload: File;
  formGroup: UntypedFormGroup;
  messages: ProgramMessage[] = [];
  option = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link']
    ]
  };
  scrollTop: number;
  hasStatusChanged = signal<boolean>(false);

  private _selectedStatus: StatusItem;

  constructor(
    private _programMessageService: ProgramMessageService,
    private _fileNameCheckerService: FileNameCheckerService,
    private _documentDataService: DocumentDataService,
    private _eventService: EventService,
    private _translateService: TranslateService,
    private _programApplicationService: ProgramApplicationService
  ) {
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges?.participantOrganization?.previousValue?.id !== simpleChanges?.participantOrganization?.currentValue?.id) {
      this.getMessages();
    }
    this.disableEnableForm();
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      body: new UntypedFormControl()
    });
    this.disableEnableForm();
  }

  statuses() {
    if (this.programApplication?.applicationStatuses && this.programApplication?.applicationStatuses.length <= 0) {
      return [];
    }

    return this.programApplication?.applicationStatuses.map((status) => {
      return {
        name: status.name,
        color: status.color
      };
    });
  }

  onStatusChange(status: StatusItem) {
    this._selectedStatus = status;
    const hasChanged = this.programApplication.status !== status.name;
    this.hasStatusChanged.set(hasChanged);
  }

  sendAll() {
    const hasMessage = !!this.formGroup.controls['body'].value?.textControl;

    if (this.formGroup.invalid) {
      return;
    }

    if (!hasMessage && !this.hasStatusChanged()) {
      return;
    }

    if (!hasMessage && this.hasStatusChanged()) {
      this.updateProgramApplicationStatus();
      return;
    }

    const message: ProgramMessage = {
      program: this.program,
      participantOrganization: this.participantOrganization,
      message: this.formGroup.controls['body'].value.textControl,
      sentDateTime: new Date(),
      sender: this.currentUser,
      isEvaluationMessage: this.displayConfiguration.isEvaluationMessage
    };

    this._programMessageService.createMessage(this.program.id, message)
      .pipe(take(1))
      .subscribe((newMessage: ProgramMessage) => {
        this.messages.push(newMessage);
        this.formGroup.reset();
        this.refresh.emit(true);
        this.scrollToBottom();
        this.displaySuccessEvent();
      });

    this.updateProgramApplicationStatus();
  }

  reload() {
    this.getMessages();
  }

  downloadFile(documentId: string) {
    const documentToDownload1: Document = this.documents.find(doc => doc.id === documentId);
    if (documentId) {
      this._documentDataService.downloadDocumentById(documentId)
        .pipe(take(1))
        .subscribe((data: any) => {
          const documentToDownload: Document = this.documents.find(doc => doc.id === documentId);
          const name = this.getFileNameFromPath(documentToDownload.path);
          FileSaver.saveAs(data, name);
        });
    }
  }

  getFileNameFromPath(path: string) {
    return path.split('/')[path.split('/').length - 1];
  }

  changeInput(event) {
    if (event?.target?.files[0]) {
      const file: File = event.target.files[0];
      this.uploadFile(file);

    }
  }

  private uploadFile(file: File) {
    const formData = new FormData();
    const s3SafeName = this._fileNameCheckerService.s3Safe(file.name);
    formData.append('file', file, s3SafeName);
    formData.append('index', this.stageOneApplicationId);
    formData.append('systemDocumentType', DOCUMENT_TAG_TYPE.PROGRAM_PARTICIPANT_MESSAGES);
    formData.append('docTagItemId', this.stageOneApplicationId);
    formData.append('docTagType', DOCUMENT_TAG_TYPE.PROGRAM_PARTICIPANT_MESSAGES);
    formData.append('uploadPath', '');
    this._documentDataService.uploadDocuments(formData)
      .pipe(take(1))
      .subscribe(response => {
        this.getMessages();
        this.refresh.emit(true);
      });
  }

  private disableEnableForm() {
    if (this.formGroup) {
      if (!this.participantOrganization) {
        this.formGroup.disable();
      } else {
        this.formGroup.enable();
      }
    }
  }

  private getMessages() {
    if (this.program && this.participantOrganization && this.stageOneApplicationId) {
      const docTag: DocTag = {
        tagName: null,
        taggedItemId: this.stageOneApplicationId,
        type: DOCUMENT_TAG_TYPE.PROGRAM_PARTICIPANT_MESSAGES
      };
      const requests = [
        this._programMessageService.getMessages(this.program.id, this.participantOrganization.id),
        this._documentDataService.getListByTag(this.participantOrganization.id, docTag, false),
        this._documentDataService.getListByTag(this.program.sponsor.id, docTag, false)
      ];
      forkJoin(requests)
        .pipe(take(1))
        .subscribe(responses => {
          let messages: ProgramMessage[] = responses[0];
          const participantDocs: Document[] = responses[1];
          const programOwnerDocs: Document[] = responses[2];

          this.documents = [...participantDocs, ...programOwnerDocs];
          const documentMessages = this.documents.reduce((accum, doc: Document) => {
            if (doc.documentKey?.docType?.toString() === 'FILE') {
              const docMessage = new ProgramMessage({
                documentId: doc.id,
                program: this.program,
                participantOrganization: this.currentUser.organization,
                message: doc.path,
                sentDateTime: doc.created,
                sender: new User({ organization: doc.owner }),
                type: PROGRAM_MESSAGE_TYPE.API_FILE,
                created: doc.created,
                updated: doc.created
              });
              accum.push(docMessage);
            }
            return accum;
          }, []);
          messages = messages.filter(m => m.type !== PROGRAM_MESSAGE_TYPE.API_FILE);
          messages.push(...documentMessages);
          this.messages = messages.sort((a, b) => {
            if (a.created > b.created) {
              return 1;
            }
            return -1;
          });
          this.scrollToBottom();
        });
    }
  }

  private scrollToBottom() {
    if (this.messageViewPort?.nativeElement) {
      this.scrollTop = this.messageViewPort.nativeElement.scrollHeight;
    }
  }

  private displaySuccessEvent() {
    if (this.displayConfiguration.displayMessagesList) {
      return;
    }

    this._eventService.success(this._translateService.instant('program.message.message-sent-developer'));
  }

  private updateProgramApplicationStatus() {
    if (!this.hasStatusChanged() || !this.displayConfiguration.displayStatusSelector) {
      return;
    }

    this._programApplicationService.updateProgramApplicationStatus(this.programApplication, this._selectedStatus.name)
      .pipe(take(1))
      .subscribe(() => {
        this._eventService.success(this._translateService.instant('program.pre-qualification.status-change.saved'));
      });
  }
}
